"use client";

import { usePGlite } from "@electric-sql/pglite-react";
import Editor, { type Monaco } from "@monaco-editor/react";
import type { editor } from "monaco-editor";
import {
  PlayIcon,
  CircleStackIcon,
  ArrowDownTrayIcon,
  CommandLineIcon,
} from "@heroicons/react/24/solid";
import { useSqlDashboardStore } from "@/lib/store";
import { seedDatabase } from "@/lib/seed";
import { toast } from "sonner";
import { useTheme } from "next-themes";
import { useRef, useState, useCallback } from "react";

const SQL_KEYWORDS = [
  "SELECT","FROM","WHERE","INSERT","INTO","VALUES","UPDATE","SET","DELETE",
  "CREATE","TABLE","DROP","ALTER","ADD","COLUMN","INDEX","VIEW","JOIN",
  "INNER","LEFT","RIGHT","OUTER","FULL","CROSS","ON","AS","AND","OR","NOT",
  "IN","EXISTS","BETWEEN","LIKE","ILIKE","IS","NULL","TRUE","FALSE",
  "ORDER","BY","ASC","DESC","GROUP","HAVING","LIMIT","OFFSET","UNION",
  "ALL","DISTINCT","CASE","WHEN","THEN","ELSE","END","COUNT","SUM","AVG",
  "MIN","MAX","PRIMARY","KEY","FOREIGN","REFERENCES","UNIQUE","CHECK",
  "DEFAULT","CONSTRAINT","CASCADE","SERIAL","INTEGER","TEXT","VARCHAR",
  "BOOLEAN","DECIMAL","TIMESTAMP","DATE","TIME","FLOAT","BIGINT","SMALLINT",
  "WITH","RECURSIVE","RETURNING","COALESCE","NULLIF","CAST","EXTRACT",
  "NOW","CURRENT_TIMESTAMP","CURRENT_DATE","BEGIN","COMMIT","ROLLBACK",
  "TRANSACTION","GRANT","REVOKE","TRUNCATE","EXPLAIN","ANALYZE","VACUUM",
  "IF","REPLACE","TEMP","TEMPORARY","SCHEMA","SEQUENCE","TRIGGER","FUNCTION",
];

function defineLightTheme(monaco: Monaco) {
  monaco.editor.defineTheme("pg-light", {
    base: "vs",
    inherit: true,
    rules: [
      { token: "keyword", foreground: "7c3aed", fontStyle: "bold" },
      { token: "operator.sql", foreground: "7c3aed" },
      { token: "string.sql", foreground: "16a34a" },
      { token: "string", foreground: "16a34a" },
      { token: "number", foreground: "d97706" },
      { token: "number.sql", foreground: "d97706" },
      { token: "comment", foreground: "94a3b8", fontStyle: "italic" },
      { token: "comment.sql", foreground: "94a3b8", fontStyle: "italic" },
      { token: "predefined.sql", foreground: "0891b2" },
      { token: "identifier", foreground: "1e293b" },
    ],
    colors: {
      "editor.background": "#ffffff",
      "editor.foreground": "#1e293b",
      "editor.lineHighlightBackground": "#f1f5f920",
      "editor.selectionBackground": "#7c3aed20",
      "editor.inactiveSelectionBackground": "#7c3aed10",
      "editorCursor.foreground": "#7c3aed",
      "editorLineNumber.foreground": "#cbd5e1",
      "editorLineNumber.activeForeground": "#7c3aed",
      "editorIndentGuide.background": "#f1f5f9",
      "editorIndentGuide.activeBackground": "#e2e8f0",
      "editorBracketMatch.background": "#7c3aed15",
      "editorBracketMatch.border": "#7c3aed40",
      "editor.wordHighlightBackground": "#7c3aed10",
      "editorGutter.background": "#ffffff",
      "editorWidget.background": "#ffffff",
      "editorWidget.border": "#e2e8f0",
      "input.background": "#f8fafc",
      "input.border": "#e2e8f0",
      "focusBorder": "#7c3aed60",
      "list.hoverBackground": "#f1f5f9",
      "list.activeSelectionBackground": "#7c3aed15",
      "list.activeSelectionForeground": "#1e293b",
      "editorSuggestWidget.background": "#ffffff",
      "editorSuggestWidget.border": "#e2e8f0",
      "editorSuggestWidget.selectedBackground": "#f1f5f9",
    },
  });
}

function defineDarkTheme(monaco: Monaco) {
  monaco.editor.defineTheme("pg-dark", {
    base: "vs-dark",
    inherit: true,
    rules: [
      { token: "keyword", foreground: "a78bfa", fontStyle: "bold" },
      { token: "operator.sql", foreground: "a78bfa" },
      { token: "string.sql", foreground: "4ade80" },
      { token: "string", foreground: "4ade80" },
      { token: "number", foreground: "fbbf24" },
      { token: "number.sql", foreground: "fbbf24" },
      { token: "comment", foreground: "64748b", fontStyle: "italic" },
      { token: "comment.sql", foreground: "64748b", fontStyle: "italic" },
      { token: "predefined.sql", foreground: "22d3ee" },
      { token: "identifier", foreground: "e2e8f0" },
    ],
    colors: {
      "editor.background": "#0f1219",
      "editor.foreground": "#e2e8f0",
      "editor.lineHighlightBackground": "#1e293b50",
      "editor.selectionBackground": "#a78bfa25",
      "editor.inactiveSelectionBackground": "#a78bfa15",
      "editorCursor.foreground": "#a78bfa",
      "editorLineNumber.foreground": "#334155",
      "editorLineNumber.activeForeground": "#a78bfa",
      "editorIndentGuide.background": "#1e293b",
      "editorIndentGuide.activeBackground": "#334155",
      "editorBracketMatch.background": "#a78bfa20",
      "editorBracketMatch.border": "#a78bfa50",
      "editor.wordHighlightBackground": "#a78bfa15",
      "editorGutter.background": "#0f1219",
      "editorWidget.background": "#141822",
      "editorWidget.border": "#1e293b",
      "input.background": "#1e293b",
      "input.border": "#334155",
      "focusBorder": "#a78bfa60",
      "list.hoverBackground": "#1e293b",
      "list.activeSelectionBackground": "#a78bfa20",
      "list.activeSelectionForeground": "#e2e8f0",
      "editorSuggestWidget.background": "#141822",
      "editorSuggestWidget.border": "#1e293b",
      "editorSuggestWidget.selectedBackground": "#1e293b",
    },
  });
}

function registerSqlCompletions(monaco: Monaco) {
  monaco.languages.registerCompletionItemProvider("sql", {
    provideCompletionItems: (model: any, position: any) => {
      const word = model.getWordUntilPosition(position);
      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };

      const keywords: any[] = SQL_KEYWORDS.map((kw) => ({
        label: kw,
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: kw,
        range,
        detail: "SQL keyword",
      }));

      const snippets: any[] = [
        {
          label: "SELECT FROM",
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: "SELECT ${1:*} FROM ${2:table_name}${3: WHERE ${4:condition}};",
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          range,
          detail: "Select query template",
        },
        {
          label: "INSERT INTO",
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: "INSERT INTO ${1:table_name} (${2:columns}) VALUES (${3:values});",
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          range,
          detail: "Insert query template",
        },
        {
          label: "CREATE TABLE",
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: [
            "CREATE TABLE ${1:table_name} (",
            "\tid SERIAL PRIMARY KEY,",
            "\t${2:column_name} ${3:TEXT} ${4:NOT NULL}",
            ");",
          ].join("\n"),
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          range,
          detail: "Create table template",
        },
        {
          label: "UPDATE SET",
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: "UPDATE ${1:table_name} SET ${2:column} = ${3:value} WHERE ${4:condition};",
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          range,
          detail: "Update query template",
        },
        {
          label: "JOIN",
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: "${1:LEFT }JOIN ${2:table_name} ON ${3:condition}",
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          range,
          detail: "Join clause template",
        },
      ];

      return { suggestions: [...keywords, ...snippets] };
    },
  });
}

export function SqlEditor() {
  const db = usePGlite();
  const { resolvedTheme } = useTheme();
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const [cursorPosition, setCursorPosition] = useState({ line: 1, col: 1 });
  const {
    currentQuery,
    setQuery,
    setResults,
    setIsExecuting,
    setError,
    isExecuting,
    addToHistory,
    triggerSidebarRefresh,
  } = useSqlDashboardStore();

  const handleRunQuery = useCallback(async () => {
    const query = useSqlDashboardStore.getState().currentQuery;
    if (!db || !query.trim()) {
      if (!query.trim()) {
        toast.warning("Please enter a SQL query");
      }
      return;
    }
    setIsExecuting(true);
    setError(null);
    setResults(null);

    try {
      await new Promise((r) => setTimeout(r, 300));
      const res = await db.query(query);

      setResults({
        rows: res.rows as any[],
        fields: res.fields,
        affectedRows: res.affectedRows,
      });

      addToHistory(query);
      triggerSidebarRefresh();

      const rowCount = (res.rows as any[]).length;
      if (rowCount > 0) {
        toast.success(`Query returned ${rowCount} row${rowCount !== 1 ? "s" : ""}`);
      } else if (res.affectedRows !== undefined && res.affectedRows > 0) {
        toast.success(`Query executed — ${res.affectedRows} row${res.affectedRows !== 1 ? "s" : ""} affected`);
      } else {
        toast.success("Query executed successfully");
      }
    } catch (err: any) {
      const message = err.message || "An unknown error occurred";
      setError(message);
      toast.error("Query failed", { description: message });
    } finally {
      setIsExecuting(false);
    }
  }, [db, setIsExecuting, setError, setResults, addToHistory, triggerSidebarRefresh]);

  const handleDownload = useCallback(() => {
    const query = useSqlDashboardStore.getState().currentQuery;
    if (!query.trim()) {
      toast.warning("Nothing to download — editor is empty");
      return;
    }
    const blob = new Blob([query], { type: "application/sql" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `query_${new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-")}.sql`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("SQL file downloaded");
  }, []);

  const handleSeed = async () => {
    if (!db) return;
    setIsExecuting(true);
    try {
      await db.exec(seedDatabase);
      triggerSidebarRefresh();
      toast.success("Database seeded with sample data");
    } catch (err: any) {
      setError(err.message);
      toast.error("Failed to seed database", { description: err.message });
    } finally {
      setIsExecuting(false);
    }
  };

  const handleBeforeMount = (monaco: Monaco) => {
    defineLightTheme(monaco);
    defineDarkTheme(monaco);
    registerSqlCompletions(monaco);
  };

  const handleEditorMount = (
    editorInstance: editor.IStandaloneCodeEditor,
    monaco: Monaco
  ) => {
    editorRef.current = editorInstance;

    // Cmd/Ctrl + Enter to run query
    editorInstance.addCommand(
      monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter,
      () => handleRunQuery()
    );

    // Cmd/Ctrl + S to download
    editorInstance.addCommand(
      monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
      () => handleDownload()
    );

    // Track cursor position
    editorInstance.onDidChangeCursorPosition((e) => {
      setCursorPosition({
        line: e.position.lineNumber,
        col: e.position.column,
      });
    });

    editorInstance.focus();
  };

  const isMac = typeof navigator !== "undefined" && /Mac/.test(navigator.userAgent);
  const modKey = isMac ? "⌘" : "Ctrl";

  return (
    <div className="h-full flex flex-col bg-card">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border bg-muted/30 shrink-0">
        <div className="flex items-center gap-2">
          <CommandLineIcon className="w-3.5 h-3.5 text-primary/60" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Query Editor
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setQuery("")}
            className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
          >
            Clear
          </button>
          <div className="w-px h-4 bg-border mx-0.5" />
          <button
            onClick={handleDownload}
            title={`Download SQL (${modKey}+S)`}
            className="flex items-center gap-1.5 px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
          >
            <ArrowDownTrayIcon className="w-3 h-3" />
            <span className="hidden sm:inline">Save .sql</span>
          </button>
          <div className="w-px h-4 bg-border mx-0.5" />
          <button
            onClick={handleSeed}
            disabled={isExecuting}
            className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-md transition-all active:scale-[0.97] disabled:opacity-50 border border-border/50"
          >
            <CircleStackIcon className="w-3 h-3" />
            Seed DB
          </button>
          <button
            onClick={handleRunQuery}
            disabled={isExecuting}
            className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-all shadow-sm active:scale-[0.97] disabled:opacity-50"
          >
            <PlayIcon className="w-3 h-3" />
            {isExecuting ? "Running..." : "Run"}
            {!isExecuting && (
              <kbd className="ml-1 text-[10px] opacity-60 font-mono">
                {modKey}↵
              </kbd>
            )}
          </button>
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-hidden relative">
        <Editor
          height="100%"
          defaultLanguage="sql"
          theme={resolvedTheme === "dark" ? "pg-dark" : "pg-light"}
          value={currentQuery}
          onChange={(value) => setQuery(value || "")}
          beforeMount={handleBeforeMount}
          onMount={handleEditorMount}
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            lineNumbers: "on",
            scrollBeyondLastLine: false,
            automaticLayout: true,
            padding: { top: 16, bottom: 16 },
            fontFamily: "'Geist Mono', 'JetBrains Mono', 'Fira Code', monospace",
            fontLigatures: true,
            roundedSelection: true,
            renderLineHighlight: "line",
            lineHeight: 22,
            letterSpacing: 0.3,
            cursorBlinking: "smooth",
            cursorSmoothCaretAnimation: "on",
            cursorWidth: 2,
            smoothScrolling: true,
            tabSize: 2,
            wordWrap: "on",
            suggest: {
              showKeywords: true,
              showSnippets: true,
              preview: true,
              filterGraceful: true,
            },
            quickSuggestions: {
              other: true,
              comments: false,
              strings: false,
            },
            bracketPairColorization: { enabled: true },
            guides: {
              bracketPairs: true,
              indentation: true,
            },
            overviewRulerBorder: false,
            hideCursorInOverviewRuler: true,
            scrollbar: {
              verticalScrollbarSize: 8,
              horizontalScrollbarSize: 8,
              vertical: "auto",
              horizontal: "auto",
            },
            renderWhitespace: "none",
            contextmenu: true,
            folding: true,
            foldingStrategy: "indentation",
          }}
        />
      </div>

      {/* Status bar */}
      <div className="flex items-center justify-between px-3 py-1 border-t border-border bg-muted/20 shrink-0 text-[11px] text-muted-foreground">
        <div className="flex items-center gap-3">
          <span>
            Ln {cursorPosition.line}, Col {cursorPosition.col}
          </span>
          <span className="w-px h-3 bg-border" />
          <span>SQL</span>
          <span className="w-px h-3 bg-border" />
          <span>
            {currentQuery.split("\n").length} line{currentQuery.split("\n").length !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="opacity-60">
            {modKey}+Enter Run
          </span>
          <span className="opacity-60">
            {modKey}+S Save
          </span>
        </div>
      </div>
    </div>
  );
}
